import makeStyles from '@mui/styles/makeStyles';
import {completedStepIds, orderSessionSteps, sessionCompletionPercentage, sessionDurationHumanReadable} from "business/session";
import classNames from "classnames";
import {convertUrl} from "common/helpers";
import {history} from 'common/history';
import {color, font, radius, space, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import MTMarkdown from "components/MTMarkdown";
import CtaButton from "components/redesign/CtaButton";
import Chip from "components/redesign/Chip";
import ShimmerImage from "components/redesign/ShimmerImage";
import Skeleton from "components/redesign/Skeleton";
import {Bookmark, Check, Play} from "lucide-react";
import {find, get, isEmpty, map, toNumber, union} from "lodash";
import React from 'react';
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
  },
  back: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT_MUTED,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: '11px 0',
    cursor: 'pointer',
  },
  content: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
    padding: '8px 0 60px',
    display: 'flex',
    flexDirection: 'column',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  spacer: {
    flex: 1,
  },
  // A page title's worth of type pushed the overview and the step list off the
  // fold; the banner underneath already carries the weight of the header.
  title: {
    ...type.panelTitle,
    fontSize: 28,
    lineHeight: '34px',
    color: color.TEXT,
    margin: 0,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 12,
  },
  tag: {
    ...type.chipSmall,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    borderRadius: 10,
    padding: '4px 9px',
  },
  duration: {
    ...type.meta,
    color: color.TEXT_MUTED,
    margin: '10px 0 0',
  },
  progressTrack: {
    height: 5,
    borderRadius: 3,
    background: color.DIVIDER,
    overflow: 'hidden',
    marginTop: 14,
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 3,
    background: color.ACCENT,
  },
  media: {
    marginTop: 18,
    width: 'fit-content',
  },
  // The height is the constraint; the width follows the banner's own ratio.
  banner: {
    height: 280,
    background: color.PAPER,
    border: `1px solid ${color.BORDER}`,
    borderRadius: radius.PANEL,
    boxSizing: 'border-box',
  },
  bookmark: {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: radius.PILL,
    border: 'none',
    background: 'none',
    color: color.ACCENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  actions: {
    display: 'flex',
    paddingTop: 16.69,
  },
  section: {
    marginTop: 28,
    paddingTop: 16,
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  sectionLabel: {
    ...type.eyebrow,
    fontWeight: font.BOLD,
    letterSpacing: '1.43px',
    color: color.SECTION_TITLE,
    margin: '0 0 8px',
  },
  overview: {
    ...type.body,
    color: color.TEXT_MUTED,
  },
  steps: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  step: {
    appearance: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px 12px',
    border: 'none',
    borderBottom: `1px solid ${color.BORDER}`,
    borderRadius: radius.CONTROL,
    cursor: 'pointer',
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  stepNumber: {
    ...type.meta,
    width: 28,
    height: 28,
    flexShrink: 0,
    borderRadius: 14,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Matches the mentorship session list: a finished step trades its number for
  // a tick, the one to resume on is filled rather than tinted.
  stepDone: {
    background: color.SUCCESS,
    color: color.SURFACE,
  },
  stepCurrent: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
  },
  stepBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  stepTitle: {
    ...type.cardHeading,
    letterSpacing: '-0.18px',
    color: color.TEXT,
    margin: 0,
  },
  stepDuration: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '34px 0',
  },
});

const SessionIntroduction = ({
  mySessions,
  allSessions,
  currentSession,
  sessionProgress,
  toggleBookmark,
  match,
  initSession,
  startSession,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const sessionId = toNumber(get(match, 'params.sessionId'));
  const combinedSessions = union(allSessions, mySessions);
  const thisSession = find(combinedSessions, session => session.id === sessionId);

  if (!thisSession) {
    return (
      <div className={classes.page}>
        <div className={classes.container}>
          <div className={classNames(classes.content, classes.loading)}>
            <Skeleton width={'70%'} height={30}/>
            <Skeleton height={200}/>
            <Skeleton width={168} height={48}/>
          </div>
        </div>
      </div>
    );
  }

  const tags = (get(thisSession, 'tags') || []).filter(Boolean);
  const overview = st(thisSession.overview, thisSession.localeOverview);
  const banner = get(thisSession, 'banner');
  const isComplete = thisSession.sessionStatus === 'COMPLETED';
  const progress = isComplete ? 100 : sessionCompletionPercentage(thisSession);
  const statusChip = isComplete ? (
    <Chip variant={'status'} done>{t('sessionTagCompleted')}</Chip>
  ) : progress > 0 ? (
    <Chip variant={'status'}>{t('sessionTagInProgress')}</Chip>
  ) : null;
  // Steps come with the session detail call, which lands after the list.
  const detail = currentSession && currentSession.id === sessionId ? currentSession : {};
  const steps = orderSessionSteps(detail);
  // A finished session ticks every step: the progress call reports a step only
  // once it has been completed in this run.
  const doneStepIds = isComplete ? map(steps, step => step.id) : completedStepIds(sessionProgress);
  const currentStepId = get(find(steps, step => !doneStepIds.includes(step.id)), 'id');

  const openSession = stepId => {
    startSession(sessionId, true);
    initSession(sessionId, true);
    history.push('/session/step', stepId ? {stepId} : undefined);
  };

  return (
    <div className={classes.page}>
      <div className={`${classes.container} ui-enter`}>
        <button type={'button'} className={classes.back} onClick={() => history.goBack()}>
          {`← ${t('learningHub')}`}
        </button>

        <div className={classes.content}>
          <div className={classes.titleRow}>
            <h1 className={classes.title}>{st(thisSession.title, thisSession.localeTitle)}</h1>
            {statusChip}
            <span className={classes.spacer}/>
            <button
              type={'button'}
              className={classes.bookmark}
              aria-pressed={Boolean(thisSession.isBookmarked)}
              onClick={() => toggleBookmark(thisSession.id)}
            >
              <Bookmark size={20} fill={thisSession.isBookmarked ? 'currentColor' : 'transparent'}/>
            </button>
          </div>

          {!isEmpty(tags) && (
            <div className={classes.tags}>
              {map(tags, tag => <span key={tag} className={classes.tag}>{tag}</span>)}
            </div>
          )}

          {/* A completed session already says so on its pill. */}
          {!isComplete && (
            <p className={classes.duration}>{sessionDurationHumanReadable(thisSession)}</p>
          )}

          {progress > 0 && !isComplete && (
            <div className={classes.progressTrack}>
              <span className={classes.progressFill} style={{width: `${progress}%`}}/>
            </div>
          )}

          <div className={classes.media}>
            {Boolean(banner) && (
              <ShimmerImage
                src={convertUrl(banner)}
                fit={'natural'}
                placeholderHeight={280}
                className={classes.banner}
              />
            )}
          </div>

          <div className={classes.actions}>
            <CtaButton
              size={'large'}
              icon={<Play size={15} fill={'currentColor'}/>}
              label={isComplete ? t('retakeSession') : progress > 0 ? t('continueSession') : t('startSession')}
              onClick={() => openSession()}
            />
          </div>

          {Boolean(overview) && (
            <div className={classes.section}>
              <p className={classes.sectionLabel}>{t('aboutSession')}</p>
              <MTMarkdown className={classes.overview}>{overview}</MTMarkdown>
            </div>
          )}

          {!isEmpty(steps) && (
            <div className={classes.section}>
              <p className={classes.sectionLabel}>{t('stepsCaps')}</p>
              <ul className={classes.steps}>
                {steps.map((step, index) => (
                  <li key={step.id}>
                    <button type={'button'} className={classes.step} onClick={() => openSession(step.id)}>
                      <span
                        className={classNames(
                          classes.stepNumber,
                          doneStepIds.includes(step.id) && classes.stepDone,
                          step.id === currentStepId && classes.stepCurrent
                        )}
                      >
                        {doneStepIds.includes(step.id) ? <Check size={15} strokeWidth={3}/> : index + 1}
                      </span>
                      <span className={classes.stepBody}>
                        <span className={classes.stepTitle}>{st(step.title, step.localeTitle)}</span>
                        {Boolean(step.duration) && (
                          <span className={classes.stepDuration}>{step.duration}</span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionIntroduction;
